import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, Circle, ChevronRight, RotateCcw, X, Sparkles } from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'soap_note',
    title: 'Write a SOAP Note',
    description: 'Try writing your own session note for Sam. Fill in the S, O, A, P sections, then generate AI parent feedback to share with caregivers.',
    action: 'Go to Session Notes',
    path: '/session-notes/',
    tip: 'Once you save the note, check the Daysheet — it auto-populates Sam\'s session as "completed"!'
  },
  {
    id: 'schedule',
    title: 'Check the Schedule',
    description: 'See how Sam\'s completed session appears in the daysheet. Try adding another appointment — you can set any time and duration.',
    action: 'Go to Daysheet',
    path: '/admin/daysheet',
    tip: 'You can manually enter any start time and session duration (20, 25, 30, 45, or 60 min).'
  },
  {
    id: 'ai_activity',
    title: 'Create an AI Activity',
    description: 'Generate a play-based activity tailored to Sam\'s interests and GLP stage. Click "AI Generate" and try different settings.',
    action: 'Go to Activities',
    path: '/activities',
    tip: 'The AI uses Sam\'s GLP stage, interests, and goals to create targeted activities.'
  },
  {
    id: 'weekly_focus',
    title: 'Plan a Weekly Focus',
    description: 'Select activities for Sam\'s week and set therapy priorities. You can also generate new activities directly from here.',
    action: 'Go to Weekly Focus',
    path: '/weekly-focus/',
    tip: 'Save your weekly plan and print it for quick reference during sessions.'
  },
  {
    id: 'chunk_bank',
    title: 'Explore the Chunk Bank',
    description: 'Sam already has some language samples recorded. Try adding a new chunk — note the text, context, and communication function.',
    action: 'Go to Chunk Bank',
    path: '/chunks/',
    tip: 'Tracking chunks over time shows language progression through GLP stages.'
  },
  {
    id: 'resources',
    title: 'Share Resources with Caregivers',
    description: 'Browse the resource library for parent-friendly articles about GLP, communication strategies, and more.',
    action: 'Go to Resources',
    path: '/learn',
    tip: 'Share resources directly with parents through the Parent Portal.'
  },
  {
    id: 'report',
    title: 'Generate a Clinical Report',
    description: 'Sam has an assessment with sample data. Try generating AI clinical interpretations section-by-section — you control what gets included.',
    action: 'Go to Assessments',
    path: '/assessments/',
    tip: 'The report uses data from Sam\'s profile, assessments, and session notes. Your writing style (if uploaded) shapes the AI output.'
  }
];

export default function GuidedTour({ childId, childName, onNavigate }) {
  const [completedSteps, setCompletedSteps] = useState([]);
  const [minimized, setMinimized] = useState(false);
  const storageKey = `playfirst_tour_completed_${childId}`;

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try { setCompletedSteps(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, [childId]);

  const markComplete = (stepId) => {
    const updated = completedSteps.includes(stepId) ? completedSteps : [...completedSteps, stepId];
    setCompletedSteps(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  const resetTour = () => {
    setCompletedSteps([]);
    localStorage.removeItem(storageKey);
  };

  const allComplete = completedSteps.length >= TOUR_STEPS.length;
  const nextStep = TOUR_STEPS.find(s => !completedSteps.includes(s.id));

  const handleStepClick = (step) => {
    markComplete(step.id);
    const path = step.path.endsWith('/') ? `${step.path}${childId}` : step.path;
    onNavigate?.(path);
  };

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-20 left-4 z-40 bg-purple-600 text-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 text-sm hover:bg-purple-700 transition-colors"
        data-testid="tour-expand-btn"
      >
        <Sparkles className="w-4 h-4" />
        Tour Guide ({completedSteps.length}/{TOUR_STEPS.length})
      </button>
    );
  }

  return (
    <Card className="border-purple-200 bg-gradient-to-b from-purple-50 to-white rounded-2xl overflow-hidden" data-testid="guided-tour">
      <div className="p-4 bg-purple-600 text-white flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-base">Explore PlayFirst with {childName}</h3>
          <p className="text-purple-200 text-xs mt-0.5">
            {allComplete ? 'All done! You\'re ready to create your own profiles.' : `${completedSteps.length} of ${TOUR_STEPS.length} completed`}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={resetTour} className="p-1.5 hover:bg-purple-500 rounded-full" title="Restart tour">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setMinimized(true)} className="p-1.5 hover:bg-purple-500 rounded-full" title="Minimize">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-purple-100">
        <div
          className="h-full bg-purple-500 transition-all duration-500"
          style={{ width: `${(completedSteps.length / TOUR_STEPS.length) * 100}%` }}
        />
      </div>

      <div className="p-3 space-y-1.5 max-h-[400px] overflow-y-auto">
        {TOUR_STEPS.map((step, index) => {
          const isComplete = completedSteps.includes(step.id);
          const isNext = nextStep?.id === step.id;

          return (
            <div
              key={step.id}
              className={`rounded-xl border p-3 transition-all ${
                isNext ? 'border-purple-300 bg-purple-50 shadow-sm' :
                isComplete ? 'border-green-200 bg-green-50/50' :
                'border-stone-100 bg-white'
              }`}
              data-testid={`tour-step-${step.id}`}
            >
              <div className="flex items-start gap-2.5">
                <div className="mt-0.5 shrink-0">
                  {isComplete ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <Circle className={`w-5 h-5 ${isNext ? 'text-purple-400' : 'text-stone-300'}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className={`text-sm font-medium ${isComplete ? 'text-green-700' : 'text-stone-800'}`}>
                      {index + 1}. {step.title}
                    </h4>
                  </div>
                  {(isNext || !isComplete) && (
                    <>
                      <p className="text-xs text-stone-500 mt-1 leading-relaxed">{step.description}</p>
                      {step.tip && (
                        <p className="text-xs text-purple-600 mt-1 italic">Tip: {step.tip}</p>
                      )}
                    </>
                  )}
                  <Button
                    size="sm"
                    variant={isNext ? 'default' : 'outline'}
                    className={`mt-2 rounded-full h-7 text-xs px-3 ${isComplete ? 'border-green-200 text-green-700' : ''}`}
                    onClick={() => handleStepClick(step)}
                    data-testid={`tour-action-${step.id}`}
                  >
                    {isComplete ? 'Revisit' : step.action}
                    <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {allComplete && (
        <div className="p-4 bg-green-50 border-t border-green-200 text-center">
          <p className="text-sm text-green-700 font-medium">
            You've explored all features! You're ready to add your own clients.
          </p>
        </div>
      )}
    </Card>
  );
}
